import React, { useState, useEffect, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Spinner, Modal, Input, Alert, Column, TableSkeleton, DeleteConfirmDialog } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { MobileFilterSheet } from '@/components/layout/MobileFilterSheet';
import { Plus, Workflow, Clock, CheckCircle, XCircle, PlayCircle, Trash2, Edit3, FileText, BarChart2, Copy, Send, Undo2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { handleError } from '@/utils/error-handler';
import { Flow, FlowStatus } from '@/types/firestore';
import { getFlows, getFlow, createFlow, deleteFlow, duplicateFlow, updateFlowStatus, isFlowRunnable, getExampleFlows, EXAMPLE_FLOW_IDS } from '@/services/flowService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getExecutionsByFlow, getExecutionStats } from '@/services/executionService';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { ExecutionDialog } from '@/components/execution-wizard';

interface FlowWithStats extends Flow {
  /** Shared example flow (system-owned): run as-is; Edit/Duplicate makes an org copy. */
  isExample?: boolean;
  lastRunDisplay: string;
  successRate: number;
  executions: number;
}

// Filter column configuration for bazza/ui DataTableFilter
const filterColumnHelper = createColumnConfigHelper<FlowWithStats>();

const filterColumnsConfig = [
  filterColumnHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Name')
    .icon(FileText)
    .build(),
  filterColumnHelper
    .text()
    .id('description')
    .accessor((row) => typeof row.description === 'string' ? row.description : '')
    .displayName('Description')
    .icon(FileText)
    .build(),
  filterColumnHelper
    .option()
    .id('status')
    .accessor((row) => row.status)
    .displayName('Status')
    .icon(CheckCircle)
    .options([
      { label: 'Published', value: 'published' },
      { label: 'Active', value: 'active' },
      { label: 'Draft', value: 'draft' },
      { label: 'Archived', value: 'archived' },
    ])
    .build(),
  filterColumnHelper
    .text()
    .id('lastRunDisplay')
    .accessor((row) => row.lastRunDisplay)
    .displayName('Last Run')
    .icon(Clock)
    .build(),
  filterColumnHelper
    .number()
    .id('successRate')
    .accessor((row) => row.successRate)
    .displayName('Success Rate')
    .icon(CheckCircle)
    .min(0)
    .max(100)
    .build(),
  filterColumnHelper
    .number()
    .id('executions')
    .accessor((row) => row.executions)
    .displayName('Executions')
    .icon(BarChart2)
    .min(0)
    .max(10000)
    .build(),
] as const;

export function Flows() {
  const navigate = useNavigate();
  const { activeOrganization, currentUser, isSuperAdmin } = useAuth();
  const { success, error: toastError } = useToast();
  const [flows, setFlows] = useState<FlowWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create Flow Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDescription, setNewFlowDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // Delete Confirmation Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<{ id: string; name: string } | null>(null);

  // Run Flow Dialog State
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false);
  const [selectedFlowToRun, setSelectedFlowToRun] = useState<FlowWithStats | null>(null);

  // Duplicate Flow State (per-row loading)
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  // Publish/unpublish State (per-row loading)
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);

  // bazza/ui DataTableFilter hook
  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: flows,
    columnsConfig: filterColumnsConfig,
  });

  // Apply filters to flows data
  const filteredFlows = useMemo(() =>
    applyFilters(flows, filters, filterColumnsConfig),
    [flows, filters]
  );
  
  // Load flows
  useEffect(() => {
    console.log('[Flows] useEffect triggered', { 
      hasOrg: !!activeOrganization, 
      orgId: activeOrganization?.id 
    });
    
    if (!activeOrganization?.id) {
      console.log('[Flows] No active organization, skipping load');
      setLoading(false);
      return;
    }
    
    loadFlows();
  }, [activeOrganization?.id]);
  
  const loadFlows = async () => {
    if (!activeOrganization?.id) return;
    
    try {
      setLoading(true);
      setError('');
      
      console.log('[Flows] Loading flows for org:', activeOrganization.id);
      let flowsData = await getFlows(activeOrganization.id);

      // Super admins also see system flows
      if (isSuperAdmin) {
        try {
          const sysQuery = query(
            collection(db, 'flows'),
            where('isSystem', '==', true)
          );
          const sysSnap = await getDocs(sysQuery);
          const sysFlows = sysSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Flow));
          flowsData = [...sysFlows, ...flowsData];
        } catch (err) {
          console.warn('[Flows] Failed to load system flows:', err);
        }
      }

      // Example flows for every org, listed first. Super admins already got
      // them in the isSystem query above, so drop those duplicates.
      const examples = await getExampleFlows(activeOrganization.id).catch((err) => {
        console.warn('[Flows] Failed to load example flows:', err);
        return [] as Flow[];
      });
      flowsData = [
        ...examples.map((f) => ({ ...f, isExample: true })),
        ...flowsData.filter((f) => !EXAMPLE_FLOW_IDS.includes(f.id)),
      ];

      console.log('[Flows] Loaded flows:', flowsData.length);
      
      // Skip execution stats enrichment for now - just display flows quickly
      // We can add stats enrichment later as a background task
      const enrichedFlows: FlowWithStats[] = flowsData.map((flow) => {
        let lastRunDisplay = 'Never';
        if (flow.lastExecutedAt) {
          const diff = Date.now() - flow.lastExecutedAt.toMillis();
          const minutes = Math.floor(diff / 60000);
          const hours = Math.floor(diff / 3600000);
          const days = Math.floor(diff / 86400000);
          
          if (minutes < 1) lastRunDisplay = 'Just now';
          else if (minutes < 60) lastRunDisplay = `${minutes} min ago`;
          else if (hours < 24) lastRunDisplay = `${hours} hour${hours > 1 ? 's' : ''} ago`;
          else lastRunDisplay = `${days} day${days > 1 ? 's' : ''} ago`;
        }
        
        return {
          ...flow,
          lastRunDisplay,
          successRate: 0,
          executions: 0,
        };
      });
      
      console.log('[Flows] Enriched flows:', enrichedFlows.length);
      setFlows(enrichedFlows);
    } catch (err: any) {
      console.error('[Flows] Error loading flows:', err);
      setError(err.message || 'Failed to load flows');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateFlow = async () => {
    if (!activeOrganization?.id || !currentUser?.uid) {
      console.error('[Flows] Missing org or user:', { org: activeOrganization?.id, user: currentUser?.uid });
      return;
    }
    if (!newFlowName.trim()) {
      setCreateError('Flow name is required');
      return;
    }
    
    setCreateLoading(true);
    setCreateError('');
    
    try {
      console.log('[Flows] Creating flow:', newFlowName);
      const flowData: any = {
        name: newFlowName.trim(),
        nodes: [],
        edges: [],
        tags: [],
      };
      
      // Only add description if it's not empty (Firestore doesn't accept undefined)
      if (newFlowDescription.trim()) {
        flowData.description = newFlowDescription.trim();
      }
      
      const newFlow = await createFlow(activeOrganization.id, currentUser.uid, flowData);
      console.log('[Flows] Flow created:', newFlow.id);
      
      // Close modal and reset form
      setIsCreateModalOpen(false);
      setNewFlowName('');
      setNewFlowDescription('');
      
      // Show success message
      success('Flow created successfully!');
      
      // Navigate directly to flow editor instead of reloading list
      console.log('[Flows] Navigating to editor:', `/flows/${newFlow.id}`);
      navigate(`/flows/${newFlow.id}`);
    } catch (err: any) {
      console.error('[Flows] Error creating flow:', err);
      const appError = handleError(err);
      setCreateError(appError.userMessage);
      toastError(appError.userMessage, 'Create Failed');
    } finally {
      setCreateLoading(false);
    }
  };
  
  const handleDeleteFlow = (flowId: string, flowName: string) => {
    setFlowToDelete({ id: flowId, name: flowName });
    setIsDeleteModalOpen(true);
  };
  
  const confirmDeleteFlow = async () => {
    if (!activeOrganization?.id || !flowToDelete) return;
    
    try {
      await deleteFlow(flowToDelete.id, activeOrganization.id);
      success('Flow deleted successfully');
      await loadFlows();
    } catch (err: any) {
      console.error('Error deleting flow:', err);
      const appError = handleError(err);
      toastError(appError.userMessage, 'Delete Failed');
    }
  };
  
  const cancelDeleteFlow = () => {
    setIsDeleteModalOpen(false);
    setFlowToDelete(null);
  };

  const handleRunFlow = (flow: FlowWithStats) => {
    setSelectedFlowToRun(flow);
    setIsRunDialogOpen(true);
  };

  const handleDuplicateFlow = async (flow: FlowWithStats) => {
    if (!activeOrganization?.id || !currentUser?.uid || duplicatingId) return;

    setDuplicatingId(flow.id);
    try {
      // Examples are copied without the "Example: " prefix — the copy is the org's own flow.
      const copy = await duplicateFlow(
        flow.id,
        activeOrganization.id,
        currentUser.uid,
        flow.isExample ? flow.name.replace(/^Example:\s*/, '') : undefined,
      );
      success(`Duplicated as "${copy.name}"`);
      await loadFlows();
      return copy;
    } catch (err: any) {
      console.error('Error duplicating flow:', err);
      const appError = handleError(err);
      toastError(appError.userMessage, 'Duplicate Failed');
    } finally {
      setDuplicatingId(null);
    }
  };

  // Examples are shared and read-only: editing one edits the org's own copy.
  const handleEditFlow = async (flow: FlowWithStats) => {
    if (!flow.isExample) {
      navigate(`/flows/${flow.id}`);
      return;
    }
    const copy = await handleDuplicateFlow(flow);
    if (copy) navigate(`/flows/${copy.id}`);
  };

  const handleTogglePublish = async (flow: FlowWithStats) => {
    if (!activeOrganization?.id || statusUpdatingId) return;
    const publish = !isFlowRunnable(flow.status);
    const nextStatus: FlowStatus = publish ? 'published' : 'draft';

    setStatusUpdatingId(flow.id);
    try {
      await updateFlowStatus(flow.id, activeOrganization.id, nextStatus);
      success(publish ? `Published "${flow.name}"` : `Moved "${flow.name}" to draft`);
      await loadFlows();
    } catch (err: any) {
      console.error('Error updating flow status:', err);
      const appError = handleError(err);
      toastError(appError.userMessage, 'Status Update Failed');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleRunStarted = (runId: string) => {
    success('Test run started successfully');
    navigate('/tests');
  };
  
  // Calculate statistics
  // Counts are the org's own flows; shared examples aren't included.
  const ownFlows = flows.filter(f => !f.isExample);
  const stats = {
    total: ownFlows.length,
    published: ownFlows.filter(f => f.status === 'active' || f.status === 'published').length,
    draft: ownFlows.filter(f => f.status === 'draft').length,
    archived: ownFlows.filter(f => f.status === 'archived').length,
  };
  
  // Define table columns
  const columns: Column<FlowWithStats>[] = [
    {
      key: 'name',
      header: 'Flow Name',
      sortable: true,
      render: (_, flow) => (
        <div>
          <div className="flex items-center gap-2 font-medium text-foreground">
            {flow.name}
            {flow.isExample && <Badge variant="outline" className="text-xs">Example</Badge>}
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {typeof flow.description === 'string' ? flow.description : 'No description'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_, flow) => (
        <Badge
          variant={
            (flow.status === 'active' || flow.status === 'published') ? 'success' :
            flow.status === 'draft' ? 'warning' :
            'secondary'
          }
        >
          {flow.status}
        </Badge>
      ),
    },
    {
      key: 'lastRunDisplay',
      header: 'Last Run',
      sortable: true,
    },
    {
      key: 'successRate',
      header: 'Success Rate',
      sortable: true,
      render: (_, flow) => (
        <span className="text-accent-green font-medium">
          {flow.successRate > 0 ? `${flow.successRate}%` : '-'}
        </span>
      ),
    },
    {
      key: 'executions',
      header: 'Executions',
      sortable: true,
      render: (_, flow) => flow.executions.toLocaleString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, flow) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            icon={<PlayCircle className="w-4 h-4 text-accent-green" />}
            onClick={(e) => {
              e.stopPropagation();
              handleRunFlow(flow);
            }}
            title="Run flow"
          />
          {!flow.id.startsWith('sys_') && !(flow as any).isSystem && (
            <Button
              variant="ghost"
              size="sm"
              icon={statusUpdatingId === flow.id
                ? <Spinner size="sm" />
                : isFlowRunnable(flow.status)
                  ? <Undo2 className="w-4 h-4 text-muted-foreground" />
                  : <Send className="w-4 h-4 text-accent-green" />}
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePublish(flow);
              }}
              disabled={statusUpdatingId === flow.id}
              title={isFlowRunnable(flow.status)
                ? 'Move to draft (hide from Run/Schedule picker)'
                : 'Publish (show in Run/Schedule picker)'}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={<Edit3 className="w-4 h-4" />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditFlow(flow);
            }}
            disabled={duplicatingId === flow.id}
            title={flow.isExample ? 'Edit a copy of this example' : 'Edit flow'}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={duplicatingId === flow.id
              ? <Spinner size="sm" />
              : <Copy className="w-4 h-4" />}
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateFlow(flow);
            }}
            disabled={duplicatingId === flow.id}
            title="Duplicate flow"
          />
          {!flow.isExample && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 className="w-4 h-4 text-accent-red" />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFlow(flow.id, flow.name);
              }}
              title="Delete flow"
            />
          )}
        </div>
      ),
    },
  ];
  
  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              Test Flows
            </h1>
            <p className="text-body text-muted-foreground">
              Create and manage automated testing workflows
            </p>
          </div>

          <Button
            variant="default"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Create Flow
          </Button>
        </div>
        
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            {error}
          </Alert>
        )}
        
        {/* Loading State */}
        {loading && (
          <Card padding="lg">
            <TableSkeleton rows={5} />
          </Card>
        )}
        
        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <Workflow className="w-5 h-5 text-accent-blue" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Total Flows</div>
                  <div className="text-h2 font-mono font-bold text-foreground">{stats.total}</div>
                </div>
              </div>
            </Card>
            
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent-green" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Published</div>
                  <div className="text-h2 font-mono font-bold text-accent-green">{stats.published}</div>
                </div>
              </div>
            </Card>
            
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-accent-orange" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Draft</div>
                  <div className="text-h2 font-mono font-bold text-accent-orange">{stats.draft}</div>
                </div>
              </div>
            </Card>
            
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-muted-foreground/70" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Archived</div>
                  <div className="text-h2 font-mono font-bold text-muted-foreground/70">{stats.archived}</div>
                </div>
              </div>
            </Card>
          </div>
        )}
        
        {/* Filters */}
        {!loading && (
          <MobileFilterSheet activeCount={filters.length}>
            <Card className="p-4">
              <DataTableFilter
                columns={filterColumns}
                filters={filters}
                actions={actions}
                strategy={strategy}
              />
            </Card>
          </MobileFilterSheet>
        )}
        
        {/* Flows Table */}
        {!loading && (
          <Card padding="lg">
            <ResponsiveTable
              columns={columns}
              data={filteredFlows}
              keyExtractor={(flow) => flow.id}
              // Examples have no row click — opening one would silently create a copy.
              onRowClick={(flow) => { if (!flow.isExample) navigate(`/flows/${flow.id}`); }}
              searchable
              searchPlaceholder="Search flows by name or description..."
              emptyMessage={
                filters.length > 0
                  ? 'No flows match the current filters'
                  : 'Get started by creating your first test flow'
              }
              paginated
              defaultPageSize={10}
            />
          </Card>
        )}
      </div>
      
      {/* Create Flow Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setNewFlowName('');
          setNewFlowDescription('');
          setCreateError('');
        }}
        title="Create New Flow"
        size="md"
      >
        <div className="space-y-4">
          {createError && (
            <Alert variant="destructive">
              {createError}
            </Alert>
          )}
          
          <Input
            label="Flow Name"
            value={newFlowName}
            onChange={(e) => setNewFlowName(e.target.value)}
            placeholder="e.g., Network Registration Test"
            required
            fullWidth
            disabled={createLoading}
          />
          
          <Input
            label="Description (Optional)"
            value={newFlowDescription}
            onChange={(e) => setNewFlowDescription(e.target.value)}
            placeholder="Brief description of what this flow tests"
            fullWidth
            disabled={createLoading}
          />
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                setNewFlowName('');
                setNewFlowDescription('');
                setCreateError('');
              }}
              disabled={createLoading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreateFlow}
              disabled={createLoading || !newFlowName.trim()}
            >
              {createLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Flow'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={isDeleteModalOpen}
        onClose={cancelDeleteFlow}
        onConfirm={confirmDeleteFlow}
        itemName={flowToDelete?.name}
        details="This action cannot be undone."
      />

      {/* Run Flow Dialog */}
      <ExecutionDialog
        isOpen={isRunDialogOpen}
        onClose={() => {
          setIsRunDialogOpen(false);
          setSelectedFlowToRun(null);
        }}
        preselectedFlow={selectedFlowToRun}
        onRunStarted={handleRunStarted}
      />
    </AppShell>
  );
}
